import type { AssessmentTab } from "../../../server/modules/homecare/assessments";
import type { ScreenContext } from "../../shell/types";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { listLink } from "../../ui/list-link";
import { openModal } from "../../ui/modal";
import { card, chip, ib, muted, table } from "../../ui/record";
import { openSend } from "../../ui/send";
import { uploadControl, type UploadedFile } from "../../ui/upload";
import { openViewer } from "../../ui/viewer";
import { area, field, refuse, select } from "../clients/forms";
import { filedHere } from "./fileit";
import { addDiagnosisModal, diagnosisNoteModal } from "./diagnoses";
import { appointmentForm } from "./appointment-form";

// The patient's Assessment tab (brief 1C-2, mockup caseRec "assess", asmView, medTable,
// woundBlock and teamBlock): Assessments (Type, Why, Start, the list), the opened assessment in
// its form's sections (questions save as they're answered; the live parts show in their place;
// Sign locks it; an addendum is signed again; a signed mini goes to the case manager), Medication
// reconciliation (the drug database note, Reconciled to completion and Reopen, the chips, the
// table, Send, Print list for the home, Add medication from bottle photos or typed), Skin and
// wounds, and Appointments and care team. Small actions are icon buttons with hover tips.

type Tab = AssessmentTab;
type Res = {
  ok: boolean;
  status: number;
  body: Tab & {
    error?: string;
    opened?: string;
    moved?: string | null;
    file?: { id: string; name: string };
    note?: string | null;
  };
};
/** An assessment as the list has it (with its addenda), and one on its own (an addendum too). */
type Root = Tab["assessments"][number];
type Assessment = Root["addenda"][number];
type Section = Assessment["sections"][number];
type Question = Section["questions"][number];
type Med = Tab["medications"]["list"][number];
type MedFilter = "all" | "change" | "flags" | "photo" | "cov";

const kv = (rows: [string, Node | string][]) =>
  el("dl", { class: "kv" }, ...rows.flatMap(([k, v]) => [el("dt", {}, k), el("dd", {}, v)]));

/** A small button with an icon and words (the mockup's "btn sm"). */
function btn(words: string, iconName: string, run: () => void, cls = "btn sm"): HTMLButtonElement {
  const b = el("button", { type: "button", class: cls }, icon(iconName), ` ${words}`);
  b.addEventListener("click", run);
  return b;
}

/**
 * Draws the Assessment tab into `host` and keeps it up to date. `changed` tells the record to
 * read itself again (the stage and the step strip move when an initial assessment is signed).
 */
export async function assessmentTab(
  host: HTMLElement,
  ctx: ScreenContext,
  patientId: string,
  changed: () => void,
) {
  const base = `/api/patients/${encodeURIComponent(patientId)}`;
  const first = (await api<Tab>("GET", `${base}/assessment`)) as Res;
  if (!first.ok) {
    host.replaceChildren(
      el(
        "section",
        { class: "card" },
        el("p", { class: "muted" }, first.body.error ?? "The assessment can't be opened."),
      ),
    );
    return;
  }
  let data: Tab = first.body;
  let opened: string | null = null;
  let medFilter: MedFilter = "all";
  const readingOn = ctx.me.ai.reading === "on";

  async function took(res: Res, done?: string): Promise<Res> {
    if (res.ok) {
      data = res.body;
      if (res.body.opened) opened = res.body.opened;
      draw();
      if (done) toast(done, res.body.note ?? undefined);
    } else toast("That wasn't saved", res.body.error);
    return res;
  }
  const send = async (method: string, path: string, body?: unknown, done?: string) =>
    took((await api<Tab>(method, `${base}${path}`, body)) as Res, done);

  // ---- Assessments --------------------------------------------------------------------------------

  function listCard(): HTMLElement {
    const body: Node[] = [];
    if (data.canEdit) {
      body.push(
        el(
          "div",
          { class: "actions", style: "margin:0" },
          btn("Start an assessment", "plus", startModal, "btn pri"),
        ),
        el(
          "p",
          { class: "small muted", style: "margin:4px 0 0" },
          listLink("assessment_type", "Change the types"),
          " · ",
          listLink("assessment_reason", "Change the reasons"),
        ),
      );
    }
    if (data.clock && data.clock.state !== "none") {
      const level =
        data.clock.state === "overdue" ? "bad" : data.clock.state === "warn" ? "warn" : "plain";
      body.push(
        el(
          "p",
          { class: "small", style: "display:flex;gap:6px;align-items:center;flex-wrap:wrap" },
          icon("clock"),
          chip(data.clock.words, level),
          el("span", { class: "muted" }, data.clock.why ?? ""),
          data.canEdit ? ib("alert", "Mark a change in condition", markChange) : null,
        ),
      );
    }
    body.push(
      data.assessments.length
        ? table(
            [
              {
                h: "Type",
                f: (a: Root) =>
                  el(
                    "span",
                    {},
                    el("b", {}, a.label),
                    el("br"),
                    el("span", { class: "small muted" }, a.typeName),
                  ),
              },
              { h: "Reason", f: (a) => a.why },
              { h: "By", f: (a) => a.by },
              { h: "Date", f: (a) => a.date },
              {
                h: "Status",
                f: (a) =>
                  chip(
                    a.status === "signed" ? "Signed" : "In progress",
                    a.status === "signed" ? "ok" : "warn",
                  ),
              },
              {
                h: "",
                f: (a) =>
                  ib(
                    a.status === "signed" ? "eye" : "pencil",
                    a.status === "signed" ? "Open" : "Continue",
                    () => {
                      opened = a.id;
                      draw();
                    },
                  ),
              },
            ],
            data.assessments,
            (a) => {
              opened = a.id;
              draw();
            },
            "Assessments",
          )
        : el("p", { class: "muted" }, "Not started."),
    );
    const outside = filedHere("Outside assessments", data.filed.assessments);
    if (outside) body.push(outside);
    return card(
      "Assessments",
      "Initial, reassessment, resumption, PRN, supervisory, discharge, or a mini review of one part.",
      [],
      ...body,
    );
  }

  /**
   * Start an assessment (brief 1E-28; mockup v86 startModal): Type and Reason from the company's
   * lists, the day it was done, and either filled in here or an outside assessment attached.
   */
  function startModal() {
    const type = select(
      "Type",
      data.types.map((t) => [t.id, t.name] as const),
      data.types[0]?.id ?? "",
    );
    const why = select(
      "Reason",
      data.choices.reasons.map((r) => [r, r] as const),
      data.choices.reasons[data.assessments.length ? 1 : 0] ?? "",
    );
    const done = field("Done on", data.today, { type: "date", max: data.today });
    const how = select(
      "How",
      [
        ["here", "Fill it in here"],
        ["outside", "Outside assessment (attach it)"],
      ] as const,
      "here",
    );
    let file: UploadedFile | null = null;
    const up = uploadControl({
      kind: "patient",
      id: patientId,
      label: "Attach the assessment",
      icon: "upload",
      ...(data.outsideKind ? { fileKind: data.outsideKind } : {}),
      onUploaded: (f) => {
        file = f;
      },
    });
    const fileBox = el("div", { class: "field", hidden: true }, up.el);
    how.input.addEventListener("change", () => {
      fileBox.hidden = how.input.value !== "outside";
    });
    const go = btn(
      "Start",
      "check",
      async () => {
        if (how.input.value === "outside" && !file)
          return refuse(m.note, "Attach the outside assessment.");
        go.disabled = true;
        const res = (await api<Tab>("POST", `${base}/assessments`, {
          typeId: type.input.value,
          why: why.input.value,
          doneOn: done.input.value || null,
          ...(how.input.value === "outside" && file ? { outsideFileId: file.id } : {}),
        })) as Res;
        go.disabled = false;
        if (!res.ok) return refuse(m.note, res.body.error);
        m.close();
        void took(res, "Assessment started");
        window.scrollTo({ top: host.offsetTop - 80 });
      },
      "btn pri",
    );
    const m = openModal({
      title: "Start an assessment",
      body: [
        el(
          "div",
          { class: "form", style: "max-width:none" },
          el("div", { class: "formgrid" }, type.el, why.el, done.el, how.el),
          fileBox,
        ),
      ],
      actions: [go],
    });
  }

  function markChange() {
    const what = field("What changed", "", { maxlength: "180" });
    const save = btn(
      "Mark it",
      "check",
      async () => {
        const res = (await api<Tab>("POST", `${base}/reassess`, { what: what.input.value })) as Res;
        if (!res.ok) return refuse(m.note, res.body.error);
        m.close();
        void took(res, "A reassessment is due today");
      },
      "btn pri",
    );
    const m = openModal({
      title: "Change in condition",
      sub: "A reassessment comes due today, for the nurse to do.",
      body: [el("div", { class: "form" }, what.el)],
      actions: [save],
    });
    what.input.focus();
  }

  /** One question's control, saved as it's answered. */
  function questionInput(a: Assessment, q: Question, alone = true): HTMLElement {
    const id = `q-${a.id}-${q.key.replace(/\W/g, "_")}`;
    const save = async (value: unknown) => {
      const res = (await api<Tab>("PUT", `${base}/assessments/${a.id}`, {
        answers: { [q.key]: value },
      })) as Res;
      if (!res.ok) return toast("That answer wasn't saved", res.body.error);
      data = res.body;
    };
    let control: HTMLElement;
    if (q.answerType === "text") {
      // A question on its own in its section is told in words (a text box, with dictation and
      // writing help); one of several, such as a vital sign, is a short line.
      const t = alone
        ? el("textarea", { id, rows: "2" }, String(q.answer ?? ""))
        : el("input", { id, type: "text", value: String(q.answer ?? ""), maxlength: "1000" });
      let timer: ReturnType<typeof setTimeout> | undefined;
      t.addEventListener("input", () => {
        clearTimeout(timer);
        timer = setTimeout(() => void save(t.value), 800);
      });
      t.addEventListener("change", () => {
        clearTimeout(timer);
        void save(t.value);
      });
      control = t;
    } else if (q.answerType === "choice" || q.answerType === "yes_no") {
      const opts: [string, string][] =
        q.answerType === "yes_no"
          ? [
              ["", "–"],
              ["yes", "Yes"],
              ["no", "No"],
            ]
          : [["", "–"], ...q.options.map((o) => [o, o] as [string, string])];
      const now =
        q.answerType === "yes_no"
          ? q.answer === true
            ? "yes"
            : q.answer === false
              ? "no"
              : ""
          : String(q.answer ?? "");
      const s = el(
        "select",
        { id },
        ...opts.map(([v, w]) => el("option", { value: v, selected: v === now }, w)),
      );
      s.addEventListener("change", () => void save(s.value || null));
      control = s;
    } else {
      const i = el("input", {
        id,
        type: q.answerType === "number" ? "number" : "date",
        value: q.answer === null ? "" : String(q.answer),
      });
      i.addEventListener("change", () => void save(i.value || null));
      control = i;
    }
    return el("div", { class: "field" }, el("label", { for: id }, q.label), control);
  }

  function sectionBody(a: Assessment, s: Section, editing: boolean): Node[] {
    const out: Node[] = [];
    // Brief 1E-28: the section that asked the ADL questions shows the company's ADLs instead,
    // each with its help and how it's done.
    const adls = s.questions.some((q) => q.key.startsWith("adl."));
    const questions = adls ? s.questions.filter((q) => !q.key.startsWith("adl.")) : s.questions;
    if (questions.length)
      out.push(
        editing
          ? el(
              "div",
              { class: "form", style: "max-width:none" },
              ...questions.map((q) => questionInput(a, q, questions.length === 1)),
            )
          : kv(questions.map((q) => [q.label, q.words])),
      );
    if (adls) out.push(...adlBlock(a, editing));
    if (s.questions.some((q) => q.key === "asm.recommend")) out.push(rnEveryField(a, editing));
    // Brief 1E-28: diagnoses are added here as well as on the patient, each with a note.
    if (s.block === "diagnoses") {
      const reread = () => send("GET", "/assessment");
      out.push(
        data.diagnoses.length
          ? table(
              [
                { h: "Code", f: (d: Tab["diagnoses"][number]) => d.code },
                {
                  h: "Diagnosis",
                  f: (d) =>
                    el(
                      "span",
                      {},
                      `${d.description}${d.primary ? " (primary)" : ""}`,
                      d.note ? el("div", { class: "small muted" }, d.note) : null,
                    ),
                },
                {
                  h: "",
                  f: (d) =>
                    data.canEdit
                      ? ib("pencil", `Note on ${d.code}`, () =>
                          diagnosisNoteModal(patientId, d, reread),
                        )
                      : "",
                },
              ],
              data.diagnoses,
              undefined,
              "Diagnoses",
            )
          : el("p", { class: "muted" }, "No diagnoses on file."),
      );
      if (data.canEdit)
        out.push(
          el(
            "div",
            { class: "actions", style: "margin-top:8px" },
            btn("Add a diagnosis", "plus", () => addDiagnosisModal(patientId, reread)),
          ),
        );
    }
    if (s.block === "medications") out.push(...medBlock());
    if (s.block === "wounds") out.push(...woundBlock());
    if (s.block === "appointments") out.push(...teamBlock());
    return out;
  }

  /** The ADLs (mockup v86 asmView): help and how it's done, saved as each is answered. */
  function adlBlock(a: Assessment, editing: boolean): Node[] {
    const save = async (item: string, help: string, note: string) => {
      const res = (await api<Tab>("PUT", `${base}/assessments/${a.id}/adls`, {
        item,
        help,
        note,
      })) as Res;
      if (!res.ok) return toast("That wasn't saved", res.body.error);
      data = res.body;
    };
    const rows = a.adls.map((x) => {
      if (!editing)
        return el(
          "tr",
          {},
          el("td", {}, el("b", {}, x.item)),
          el("td", {}, chip(x.helpLabel, x.help === "independent" ? "ok" : "warn")),
          el("td", {}, x.note ?? "–"),
        );
      const help = el(
        "select",
        { "aria-label": `${x.item} help` },
        ...data.adlHelp.map((h) =>
          el("option", { value: h.value, selected: h.value === x.help }, h.label),
        ),
      );
      const note = el("input", {
        type: "text",
        "aria-label": `${x.item} note`,
        value: x.note ?? "",
        placeholder: "How it's done",
        maxlength: "300",
      });
      const go = () => void save(x.item, help.value, note.value);
      help.addEventListener("change", go);
      note.addEventListener("change", go);
      return el(
        "tr",
        {},
        el("td", {}, el("b", {}, x.item)),
        el("td", {}, help),
        el("td", {}, note),
      );
    });
    return [
      el(
        "div",
        { class: "tw" },
        el(
          "table",
          { "aria-label": "ADLs" },
          el(
            "thead",
            {},
            el("tr", {}, el("th", {}, "ADL"), el("th", {}, "Help"), el("th", {}, "How it's done")),
          ),
          el("tbody", {}, ...rows),
        ),
      ),
      el(
        "p",
        { class: "small muted", style: "margin:6px 0 0" },
        "Each one with help becomes an aide task. ",
        listLink("adl"),
      ),
    ];
  }

  /** The RN visits the assessment recommends; the drafted care plan's RN line takes it. */
  function rnEveryField(a: Assessment, editing: boolean): HTMLElement {
    if (!editing) return kv([["RN visits", a.rnEveryDays ? `Every ${a.rnEveryDays} days` : "–"]]);
    const f = field("RN visits every (days)", a.rnEveryDays ? String(a.rnEveryDays) : "", {
      type: "number",
      min: "1",
      max: "365",
      inputmode: "numeric",
    });
    f.input.addEventListener("change", async () => {
      const res = (await api<Tab>("PUT", `${base}/assessments/${a.id}`, {
        rnEveryDays: f.input.value || null,
      })) as Res;
      if (!res.ok) return toast("That wasn't saved", res.body.error);
      data = res.body;
    });
    return el("div", { class: "form", style: "max-width:none" }, f.el);
  }

  function assessmentView(root: Root): HTMLElement {
    // Edit and sign again (brief 1E-23): a signed assessment shows its version in use, or the
    // draft of its next version while it's edited (the same screen).
    const a: Assessment =
      root.status === "signed" ? (root.editing ?? root.current) : (root as Assessment);
    const editing = a.status === "draft" && data.canEdit;
    const changes = data.medications.list.filter(
      (m) => m.decision && m.decision !== "continue",
    ).length;
    const sections = a.sections.map((s, i) =>
      el(
        "details",
        {
          class: "asec",
          open: editing && (i === 0 || s.block === "medications") ? true : undefined,
        },
        el(
          "summary",
          {},
          el("span", { class: "sn" }, String(i + 1)),
          el("b", {}, s.title),
          s.block === "medications" && changes ? chip(`${changes} changes`, "warn") : null,
        ),
        el("div", { class: "asb" }, ...sectionBody(a, s, editing)),
      ),
    );
    sections.push(
      el(
        "details",
        { class: "asec", open: true },
        el(
          "summary",
          {},
          el("span", { class: "sn" }, String(a.sections.length + 1)),
          el("b", {}, "Sign-off"),
        ),
        el("div", { class: "asb" }, ...signOff(a)),
      ),
    );
    const addenda = root.addenda.map((d) =>
      el(
        "section",
        { class: "card", style: "margin-top:10px" },
        el(
          "div",
          { class: "cardhead" },
          el(
            "div",
            {},
            el("div", { class: "eyebrow" }, `Addendum · ${d.date}`),
            el("h2", {}, d.addendumNote ?? ""),
          ),
          chip(
            d.status === "signed" ? "Signed" : "In progress",
            d.status === "signed" ? "ok" : "warn",
          ),
        ),
        ...d.sections
          .filter((s) => s.questions.length)
          .map((s) =>
            el(
              "details",
              { class: "asec", open: d.status === "draft" ? true : undefined },
              el("summary", {}, el("b", {}, s.title)),
              el(
                "div",
                { class: "asb" },
                d.status === "draft" && data.canEdit
                  ? el(
                      "div",
                      { class: "form", style: "max-width:none" },
                      ...s.questions.map((q) => questionInput(d, q, s.questions.length === 1)),
                    )
                  : kv(s.questions.map((q) => [q.label, q.words])),
              ),
            ),
          ),
        el("div", { class: "asb" }, ...signOff(d)),
      ),
    );
    const back = btn(
      "All assessments",
      "arrow-right",
      () => {
        opened = null;
        draw();
      },
      "btn sm ghost",
    );
    back.querySelector("svg")?.classList.add("flip");
    const line = changeLine(root);
    return el(
      "div",
      {},
      line ?? "",
      el(
        "section",
        { class: "card" },
        el(
          "div",
          { class: "cardhead" },
          el(
            "div",
            {},
            el("div", { class: "eyebrow" }, `${a.label} · ${a.typeName}`),
            el("div", { class: "small" }, icon("info"), " Reason: ", el("b", {}, a.why)),
            el("h2", {}, `${a.by} · ${a.date}`),
          ),
          chip(
            a.status === "signed" ? "Signed" : "In progress",
            a.status === "signed" ? "ok" : "warn",
          ),
        ),
        a.form && !a.outside
          ? el(
              "p",
              { class: "small muted", style: "margin:0" },
              `${a.form.name} · version ${a.form.version}`,
            )
          : null,
        // Brief 1E-28: an outside assessment attached so it counts as this one.
        a.outside
          ? el(
              "p",
              { class: "small", style: "margin:6px 0 0" },
              icon("file"),
              " Outside assessment: ",
              (() => {
                const b = el("button", { type: "button", class: "btn sm ghost" }, a.outside.name);
                const f = a.outside;
                b.addEventListener(
                  "click",
                  () => void openViewer({ file: f, document: "Outside assessment" }),
                );
                return b;
              })(),
            )
          : null,
        a.kind === "consultation"
          ? el(
              "p",
              { class: "small muted" },
              icon("info"),
              " Non-skilled: no diagnoses, medications or wounds are recorded.",
            )
          : null,
        ...sections,
        ...addenda,
        el("div", { class: "actions", style: "margin-top:10px" }, back),
      ),
    );
  }

  /** The assessment's change line (mockup v83 changeLine): its own card, apart from the feed. */
  function changeLine(root: Root): HTMLElement | null {
    const cur = root.versions.find((v) => v.current) ?? root.versions.at(-1);
    if (!cur) return null;
    const edit = root.editing;
    return el(
      "section",
      { class: "card chgline", "aria-label": "Signed version" },
      el(
        "div",
        { class: "cardhead", style: "margin:0;align-items:center" },
        edit
          ? el(
              "div",
              {},
              el("b", {}, icon("pencil"), ` Editing assessment · version ${edit.version} draft`),
              el(
                "div",
                { class: "small muted" },
                `Version ${cur.version} stays in use until this is signed.`,
              ),
            )
          : el(
              "div",
              {},
              el("b", {}, `Assessment · version ${cur.version}`),
              el(
                "span",
                { class: "small muted" },
                ` · ${cur.version > 1 ? "updated" : "signed"} ${cur.when} by ${cur.by}`,
              ),
              root.versions.length > 1
                ? btn(
                    `${root.versions.length} versions`,
                    "history",
                    () => versionsModal(root),
                    "btn sm ghost",
                  )
                : "",
              // Brief 1E-28: each reconciliation is in the change line.
              data.medications.reconciled
                ? el("div", { class: "small muted" }, data.medications.reconciled.words)
                : "",
            ),
        data.canEdit
          ? edit
            ? el(
                "span",
                { class: "actions" },
                btn(
                  "Cancel",
                  "x",
                  () =>
                    void send("POST", `/assessments/${root.id}/cancel`, undefined, "Not signed"),
                  "btn sm",
                ),
              )
            : btn(
                "Edit",
                "pencil",
                () => void send("POST", `/assessments/${root.id}/edit`, undefined, "Editing"),
                "btn sm",
              )
          : el("span"),
      ),
    );
  }

  /** Every signed version (mockup verModal); one that isn't in use opens read-only. */
  function versionsModal(root: Root) {
    openModal({
      title: "Assessment versions",
      sub: "Each signed version is kept. Open one to see it as it was signed.",
      body: [
        table(
          [
            { h: "Version", f: (v: Root["versions"][number]) => el("b", {}, String(v.version)) },
            { h: "Signed", f: (v) => v.when },
            { h: "By", f: (v) => v.by },
            { h: "What changed", f: (v) => v.what },
            {
              h: "",
              f: (v) =>
                v.current
                  ? chip("In use", "ok")
                  : btn(
                      "Open",
                      "eye",
                      () =>
                        void openModal({
                          title: `Assessment · version ${v.version}`,
                          sub: `Read only, as signed · ${v.by} · ${v.when}`,
                          body: v.view.sections
                            .filter((x) => x.questions.length)
                            .map((x) =>
                              el(
                                "div",
                                {},
                                el("h3", { class: "h3s" }, x.title),
                                kv(x.questions.map((q) => [q.label, q.words])),
                              ),
                            ),
                          wide: true,
                        }),
                      "btn sm ghost",
                    ),
            },
          ],
          [...root.versions].reverse(),
          undefined,
          "Assessment versions",
        ),
      ],
      wide: true,
    });
  }

  function signOff(a: Assessment): Node[] {
    if (a.status === "signed") {
      const out: Node[] = [
        el(
          "p",
          {},
          chip("Signed", "ok"),
          ` ${a.signed?.signature ?? `${a.signed?.by ?? ""}${a.signed?.title ? `, ${a.signed.title}` : ""}`} · ${a.signed?.at ?? ""}.`,
        ),
      ];
      const acts: Node[] = [];
      // Brief 1E-23: a change is Edit and sign again (the change line above); no addendum.
      if (a.kind === "mini" && !a.addendumOf && data.canEdit)
        acts.push(btn("Send to the case manager", "paper-airplane", () => sendMini(a)));
      if (a.sent)
        out.push(
          el(
            "p",
            { class: "small muted" },
            `Sent to the case manager ${a.sent.at} · ${a.sent.reason ?? ""}`,
          ),
        );
      if (acts.length) out.push(el("div", { class: "actions" }, ...acts));
      return out;
    }
    if (!data.canEdit) return [el("p", { class: "muted" }, "Not signed yet.")];
    const next = data.assessments.find((x) => x.editing?.id === a.id)?.editing?.version;
    const sign = btn(
      next
        ? `Sign version ${next}`
        : `Sign ${a.addendumOf ? "addendum" : a.kind === "consultation" ? "consultation" : "assessment"}`,
      "check",
      async () => {
        sign.disabled = true;
        const res = await send(
          "POST",
          `/assessments/${a.id}/sign`,
          {
            ...(allergies ? { allergies: allergies.input.value } : {}),
            ...(codeStatus ? { codeStatus: codeStatus.input.value } : {}),
          },
          "Signed and locked",
        );
        sign.disabled = false;
        if (res.ok) {
          if (res.body.moved)
            toast("Signed and locked", `The episode moved on to ${res.body.moved}`);
          changed();
        }
      },
      "btn pri",
    );
    sign.disabled = !a.canSign;
    // Brief 1E-28: what signing checks, as the company's setting says (Flag or Block).
    const full = a.kind !== "mini" && !a.addendumOf;
    const block = data.atSigning.setting === "block";
    const meds =
      full && data.medications.list.length && !data.medications.reconciled
        ? el(
            "p",
            { class: "small", style: "margin:0 0 6px" },
            chip("Medications not reconciled", "warn"),
            block ? " Reconcile them before signing." : " Signing raises a flag.",
          )
        : null;
    const facts = data.patientFacts;
    const allergies =
      full && !facts.allergies?.trim() ? field("Allergies", "", { maxlength: "300" }) : null;
    const codeStatus =
      full && !facts.codeStatus?.trim()
        ? select("Code status", [["", "–"], ...data.codeStatuses.map((c) => [c, c] as const)], "")
        : null;
    const asked =
      allergies || codeStatus
        ? el(
            "div",
            { class: "form", style: "max-width:none" },
            el(
              "p",
              { class: "small", style: "margin:0" },
              block
                ? "Allergies and code status are needed to sign."
                : "Allergies and code status are empty. Signing without them raises a flag.",
            ),
            el("div", { class: "formgrid" }, allergies?.el ?? "", codeStatus?.el ?? ""),
          )
        : null;
    const parts: (HTMLElement | null)[] = [
      ...(a.status === "draft" ? [meds, asked] : []),
      el(
        "p",
        { class: "small muted" },
        next
          ? "The version in use stays until this is signed."
          : "Signing locks it. Use Edit to change it after that.",
      ),
      a.signRefusal
        ? el("p", { class: "small", role: "note" }, icon("lock"), ` ${a.signRefusal}`)
        : null,
      sign,
    ];
    return parts.filter((x): x is HTMLElement => x !== null);
  }

  function sendMini(a: Assessment) {
    const reason = field("Why it's going to the case manager", "", { maxlength: "300" });
    const go = btn(
      "Continue to Send",
      "paper-airplane",
      async () => {
        const res = (await api<Tab>("POST", `${base}/assessments/${a.id}/send`, {
          reason: reason.input.value,
        })) as Res;
        if (!res.ok) return refuse(m.note, res.body.error);
        m.close();
        void took(res);
        const file = res.body.file;
        if (file)
          void openSend({ files: [file], document: a.typeName, for: "Case manager", tab: "Email" });
      },
      "btn pri",
    );
    const m = openModal({
      title: "Send to the case manager",
      sub: "It goes as a secure link.",
      body: [el("div", { class: "form" }, reason.el)],
      actions: [go],
    });
    reason.input.focus();
  }

  // ---- Medication reconciliation ---------------------------------------------------------------------

  function medBlock(): Node[] {
    const M = data.medications;
    const out: Node[] = [];
    const lists = filedHere("Medication lists", data.filed.medications);
    if (lists) out.push(lists);
    // Brief 1E-28: changed and reconciled again at any time, by any clinician, signed or not.
    const editable = data.canEdit;
    if (M.list.length)
      out.push(
        el(
          "div",
          { class: "drugdb" },
          icon("shield-check"),
          el(
            "div",
            {},
            el("b", {}, "Checked against the drug references"),
            ` · RxNorm (National Library of Medicine) for names and strengths, FDA labels (DailyMed, openFDA) for warnings · ${M.matched} of ${M.list.length} matched`,
            M.warnings.length ? el("ul", {}, ...M.warnings.map((w) => el("li", {}, w.text))) : null,
          ),
        ),
      );
    if (data.canEdit || M.list.length) {
      const bar: Node[] = [];
      if (M.list.length)
        bar.push(
          M.reconciled
            ? el(
                "span",
                { class: "small" },
                icon("check-circle"),
                ` ${M.reconciled.words}${M.reconciled.note ? ` · ${M.reconciled.note}` : ""}`,
              )
            : chip("Not reconciled", "warn"),
        );
      if (data.canEdit && M.import.on) bar.push(btn("Import a list", "upload", importModal));
      if (data.canEdit && M.list.length) {
        const undecided = M.state.undecided;
        const mark = btn(
          M.reconciled ? "Reconcile again" : "Mark reconciled",
          "check",
          () => void send("POST", "/medications/reconcile", {}, "Medications reconciled"),
          undecided || M.reconciled ? "btn sm" : "btn sm pri",
        );
        mark.disabled = undecided > 0;
        if (undecided) mark.title = `${undecided} without a decision`;
        bar.push(mark);
      }
      out.push(
        el(
          "div",
          { class: "actions medbar", style: "justify-content:flex-end;margin:0 0 8px" },
          ...bar,
        ),
      );
      if (M.list.length && !M.reconciled && M.state.undecided)
        out.push(
          el(
            "p",
            { class: "small muted", style: "margin:0 0 6px" },
            `${M.state.undecided} without a decision`,
          ),
        );
    }
    if (M.list.length) {
      const count = (d: string) => M.list.filter((m) => m.decision === d).length;
      out.push(
        el(
          "div",
          { class: "linkrow" },
          chip(`${M.list.length} medications`, "plain"),
          ...(
            [
              ["changed_dose", "changed dose", "warn"],
              ["new", "new", "info"],
              ["stop", "stop", "bad"],
              ["uncertain", "uncertain", "warn"],
            ] as const
          )
            .filter(([d]) => count(d))
            .map(([d, w, lvl]) => chip(`${count(d)} ${w}`, lvl)),
        ),
      );
      const F: [MedFilter, string, (m: Med) => boolean][] = [
        ["all", "All", () => true],
        ["change", "Changes", (m) => !!m.decision && m.decision !== "continue"],
        ["flags", "Flags", (m) => m.flagged || (M.claim && m.coveredByClaim === false)],
        ["photo", "No photo", (m) => !m.photos.length],
        ...(M.claim
          ? [
              ["cov", "Covered by claim", (m: Med) => m.coveredByClaim === true] as [
                MedFilter,
                string,
                (m: Med) => boolean,
              ],
            ]
          : []),
      ];
      const pass = F.find(([k]) => k === medFilter)?.[2] ?? (() => true);
      out.push(
        el(
          "div",
          { class: "stagef", role: "group", "aria-label": "Filter" },
          ...F.map(([k, l, f]) => {
            const b = el(
              "button",
              {
                type: "button",
                class: `chipbtn${medFilter === k ? " on" : ""}`,
                "aria-pressed": String(medFilter === k),
              },
              `${l} ${M.list.filter(f).length}`,
            );
            b.addEventListener("click", () => {
              medFilter = k;
              draw();
            });
            return b;
          }),
        ),
      );
      out.push(
        table(
          [
            {
              h: "",
              f: (m: Med) => {
                const ph = m.photos[0];
                if (ph) {
                  const b = el(
                    "button",
                    {
                      type: "button",
                      class: "thumb",
                      "aria-label": `Photos of ${m.name}`,
                      "data-tip": `View photo (${m.photos.length})`,
                    },
                    icon("file-media"),
                  );
                  b.addEventListener(
                    "click",
                    () => void openViewer({ file: ph, document: `Bottle photo · ${m.name}` }),
                  );
                  return b;
                }
                if (!data.canEdit) return "";
                const b = el(
                  "button",
                  {
                    type: "button",
                    class: "thumb add",
                    "aria-label": `Take a photo of ${m.name}`,
                    "data-tip": "Take a photo of the bottle",
                  },
                  icon("device-camera"),
                );
                b.addEventListener("click", () => morePhotos(m));
                return b;
              },
            },
            {
              h: "Medication",
              f: (m) =>
                el(
                  "span",
                  {},
                  el("b", {}, m.name),
                  editable ? ib("pencil", `Change ${m.name}`, () => medModal(m)) : "",
                  [m.dose, m.routeLabel?.toLowerCase(), m.prescriber].filter(Boolean).join(" · ")
                    ? el(
                        "div",
                        { class: "small muted" },
                        [m.dose, m.routeLabel?.toLowerCase(), m.prescriber]
                          .filter(Boolean)
                          .join(" · "),
                      )
                    : null,
                  m.note ? el("div", { class: "small mnote-t" }, icon("note"), ` ${m.note}`) : null,
                ),
            },
            {
              h: "Frequency",
              f: (m) =>
                [m.frequency, m.prnFor ? `as needed for ${m.prnFor}` : ""]
                  .filter(Boolean)
                  .join(", ") || "–",
            },
            { h: "Taken in the home", f: (m) => m.takenInHome ?? "–" },
            {
              h: "Decision",
              f: (m) => {
                if (!editable)
                  return m.decisionLabel
                    ? chip(m.decisionLabel, m.decisionLevel ?? "plain")
                    : muted("No decision");
                const s = el(
                  "select",
                  { "aria-label": `Decision for ${m.name}` },
                  el("option", { value: "", selected: !m.decision, disabled: true }, "Decide…"),
                  ...M.decisions.map((d) =>
                    el("option", { value: d.value, selected: d.value === m.decision }, d.label),
                  ),
                );
                s.addEventListener("change", () => {
                  if (s.value === "uncertain" && !m.note) return noteModal(m, "uncertain");
                  void send("PUT", `/medications/${m.id}`, { decision: s.value });
                });
                return s;
              },
            },
            {
              h: M.claim ? "Claim" : "Flag",
              f: (m) =>
                M.claim
                  ? m.coveredByClaim === null
                    ? muted("–")
                    : chip(
                        m.coveredByClaim ? "Covered" : "Not covered",
                        m.coveredByClaim ? "ok" : "plain",
                      )
                  : m.warnings.length
                    ? chip(
                        m.warnings.length === 1 ? "1 warning" : `${m.warnings.length} warnings`,
                        "bad",
                      )
                    : m.decision === "uncertain"
                      ? chip("Uncertain", "warn")
                      : "",
            },
            {
              h: "",
              f: (m) =>
                editable ? ib("note", `Nurse note for ${m.name}`, () => noteModal(m)) : "",
            },
          ],
          M.list.filter(pass),
          undefined,
          "Medications",
        ),
      );
    } else out.push(el("p", { class: "muted" }, "No medications recorded."));
    const acts: Node[] = [];
    if (M.list.length && data.canEdit) {
      acts.push(
        btn("Send to care manager or prescriber", "paper-airplane", async () => {
          const res = (await api<Tab>("POST", `${base}/medications/list`, { for: "send" })) as Res;
          if (!res.ok) return toast("The list wasn't made", res.body.error);
          void took(res);
          if (res.body.file)
            void openSend({
              files: [res.body.file],
              document: "Medication reconciliation",
              tab: "Email",
            });
        }),
        btn("Print list for the home", "file", async () => {
          const res = (await api<Tab>("POST", `${base}/medications/list`, { for: "home" })) as Res;
          if (!res.ok) return toast("The list wasn't made", res.body.error);
          void took(res);
          if (res.body.file) void openViewer({ file: res.body.file, document: "Medication list" });
        }),
      );
    }
    if (editable) acts.push(btn("Add medication", "plus", addMedication));
    if (acts.length) out.push(el("div", { class: "actions", style: "margin-top:8px" }, ...acts));
    return out;
  }

  /** The pencil on a medication (mockup v86 medModal): its own fields and the decision. */
  function medModal(m: Med) {
    const f = medFields(m);
    const decision = select(
      "Decision",
      data.medications.decisions.map((d) => [d.value, d.label] as const),
      m.decision ?? "continue",
    );
    const save = btn(
      "Save",
      "check",
      async () => {
        if (decision.input.value === "uncertain" && !m.note)
          return refuse(md.note, "Uncertain needs a note: use the note button.");
        save.disabled = true;
        const res = (await api<Tab>("PUT", `${base}/medications/${m.id}`, {
          ...f.values(),
          decision: decision.input.value,
        })) as Res;
        save.disabled = false;
        if (!res.ok) return refuse(md.note, res.body.error);
        md.close();
        void took(res, "Medication saved");
      },
      "btn pri",
    );
    const md = openModal({
      title: `Change ${m.name}`,
      wide: true,
      body: [el("div", { class: "form", style: "max-width:none" }, f.grid, decision.el)],
      actions: [save],
    });
    f.name.input.focus();
  }

  /** A medication's own fields, as Add medication, the pencil and the import share them. */
  function medFields(m: Partial<Med> = {}) {
    const name = field("Medication", m.name ?? "", {
      maxlength: "200",
      placeholder: "Name and strength",
    });
    const dose = field("Dose", m.dose ?? "", { maxlength: "80", placeholder: "e.g. 10 mg" });
    const route = select(
      "Route",
      [["", "–"], ...data.medications.routes.map((r) => [r.value, r.label] as const)],
      m.route ?? "",
    );
    const freq = field("Frequency", m.frequency ?? "", {
      maxlength: "200",
      placeholder: "e.g. Twice a day, 8 AM and 8 PM",
    });
    const prescriber = field("Prescriber", m.prescriber ?? "", { maxlength: "160" });
    const started = field("Started", m.startedOn ?? "", { type: "date" });
    const prn = field("As needed for", m.prnFor ?? "", {
      maxlength: "200",
      placeholder: "Empty when taken on a schedule",
    });
    const home = field("Taken in the home", m.takenInHome ?? "", {
      maxlength: "200",
      placeholder: "How it's taken, if different",
    });
    return {
      name,
      grid: el(
        "div",
        { class: "formgrid" },
        name.el,
        dose.el,
        route.el,
        freq.el,
        prescriber.el,
        started.el,
        prn.el,
        home.el,
      ),
      values: () => ({
        name: name.input.value,
        dose: dose.input.value,
        route: route.input.value || null,
        frequency: freq.input.value,
        prescriber: prescriber.input.value,
        startedOn: started.input.value || null,
        prnFor: prn.input.value,
        takenInHome: home.input.value,
      }),
      own: () => ({
        dose: dose.input.value,
        route: route.input.value || null,
        frequency: freq.input.value,
        prescriber: prescriber.input.value,
        prnFor: prn.input.value,
        takenInHome: home.input.value,
      }),
      started,
      freq,
    };
  }

  type Line = {
    name: string;
    dose: string | null;
    route: string | null;
    frequency: string | null;
    prnFor: string | null;
    onList: boolean;
  };

  /**
   * Import a list (brief 1E-28; mockup v86 importModal): a clinic, discharge or pharmacy list is
   * read into lines (by AI when it's on, else typed or pasted), each ticked before it's added.
   */
  function importModal() {
    let fileId: string | null = null;
    let lines: Line[] = [];
    const status = el("p", { class: "small", role: "status", "aria-live": "polite" });
    const found = el("div", {});
    const paste = el("textarea", {
      id: "medimp-text",
      rows: "5",
      placeholder: "One medication a line, e.g. Amlodipine 5 mg by mouth daily",
    });
    const readTyped = btn("Read the list", "check", async () => {
      readTyped.disabled = true;
      const res = await api<{ lines: Line[]; error?: string }>(
        "POST",
        `${base}/medications/import/read`,
        { text: paste.value },
      );
      readTyped.disabled = false;
      if (!res.ok) return status.replaceChildren(res.body.error ?? "Nothing was read.");
      show(res.body.lines, false);
    });
    const up = uploadControl({
      kind: "patient",
      id: patientId,
      label: "Pick the list",
      icon: "upload",
      ...(data.medicationListKind ? { fileKind: data.medicationListKind } : {}),
      onUploaded: async (f) => {
        fileId = f.id;
        if (!readingOn)
          return status.replaceChildren(icon("file"), ` ${f.name}. Type the lines below.`);
        status.replaceChildren("AI is reading the list…");
        const res = await api<{ lines: Line[]; error?: string }>(
          "POST",
          "/api/ai/medication-list",
          {
            patient: patientId,
            file: f.id,
          },
        );
        if (!res.ok)
          return status.replaceChildren(
            res.body.error ?? "AI couldn't read it. Type the list below.",
          );
        show(res.body.lines, true);
      },
    });
    const addBtn = btn(
      "Add checked",
      "check",
      async () => {
        const boxes = [...found.querySelectorAll<HTMLInputElement>("input[type=checkbox]")];
        const ticked = lines.map((l, i) => ({ ...l, ticked: !!boxes[i]?.checked }));
        addBtn.disabled = true;
        const res = (await api<Tab>("POST", `${base}/medications/import`, {
          lines: ticked,
          ...(fileId ? { fileId } : {}),
        })) as Res;
        addBtn.disabled = false;
        if (!res.ok) return refuse(m.note, res.body.error);
        m.close();
        void took(res, "Medications added");
      },
      "btn pri",
    );
    addBtn.disabled = true;
    function show(list: Line[], byAi: boolean) {
      lines = list;
      const count = () => found.querySelectorAll("input:checked").length;
      const relabel = () => {
        addBtn.replaceChildren(icon("check"), ` Add ${count()} checked`);
        addBtn.disabled = count() === 0;
      };
      status.replaceChildren(
        byAi
          ? el("span", { class: "chip info small", "data-ai": "1" }, "Read by AI, check it")
          : "",
        ` ${list.length} line${list.length === 1 ? "" : "s"} found`,
      );
      found.replaceChildren(
        el("h3", { class: "h3s" }, "Found on the list"),
        table(
          [
            {
              h: "",
              f: (l: Line) => {
                const c = el("input", {
                  type: "checkbox",
                  checked: !l.onList,
                  "aria-label": `Add ${l.name}`,
                });
                c.addEventListener("change", relabel);
                return c;
              },
            },
            { h: "Medication", f: (l) => el("b", {}, l.name) },
            { h: "Dose", f: (l) => l.dose ?? "–" },
            {
              h: "Route",
              f: (l) => data.medications.routes.find((r) => r.value === l.route)?.label ?? "–",
            },
            {
              h: "Frequency",
              f: (l) =>
                [l.frequency, l.prnFor ? `as needed for ${l.prnFor}` : ""]
                  .filter(Boolean)
                  .join(", ") || "–",
            },
            {
              h: "",
              f: (l) => (l.onList ? chip("Already on the list", "warn") : chip("New", "info")),
            },
          ],
          list,
          undefined,
          "Found on the list",
        ),
      );
      relabel();
    }
    const m = openModal({
      title: "Import a medication list",
      sub: "A clinic list, a discharge list or a pharmacy printout. Each line is checked before it's added.",
      wide: true,
      body: [
        el(
          "div",
          { class: "form", style: "max-width:none" },
          up.el,
          status,
          el(
            "div",
            { class: "field" },
            el("label", { for: "medimp-text" }, "Or type or paste the list"),
            paste,
          ),
          el("div", { class: "actions", style: "margin:0" }, readTyped),
          found,
        ),
      ],
      actions: [addBtn],
    });
  }

  function noteModal(m: Med, decision?: "uncertain") {
    const note = area(
      decision ? "What's unclear, and who was asked" : "Nurse note for this medication",
      m.note ?? "",
    );
    const save = btn(
      "Save note",
      "check",
      async () => {
        const res = (await api<Tab>(
          "PUT",
          `${base}/medications/${m.id}`,
          decision ? { decision, note: note.input.value } : { note: note.input.value },
        )) as Res;
        if (!res.ok) return refuse(md.note, res.body.error);
        md.close();
        void took(res, decision ? "Uncertain · flagged until resolved" : "Note saved");
      },
      "btn pri",
    );
    const md = openModal({
      title: m.name,
      sub: decision ? "Uncertain needs a note. It stays flagged until it's resolved." : undefined,
      body: [el("div", { class: "form" }, note.el)],
      actions: [save],
      onClose: () => draw(),
    });
    note.input.focus();
  }

  function morePhotos(m: Med) {
    const got: UploadedFile[] = [];
    const list = el("div", { class: "photopick" });
    const up = uploadControl({
      kind: "patient",
      id: patientId,
      camera: true,
      label: "Add photo",
      icon: "device-camera",
      hint: `Up to ${data.medications.maxPhotos - m.photos.length} more`,
      onUploaded: (f) => {
        got.push(f);
        list.append(chip(f.name, "plain"));
      },
    });
    const save = btn(
      "Save photos",
      "check",
      async () => {
        const res = (await api<Tab>("POST", `${base}/medications/${m.id}/photos`, {
          files: got.map((f) => f.id),
        })) as Res;
        if (!res.ok) return refuse(md.note, res.body.error);
        md.close();
        void took(res, "Photos added");
      },
      "btn pri",
    );
    const md = openModal({ title: `Photos of ${m.name}`, body: [up.el, list], actions: [save] });
  }

  /**
   * Add medication: photos of the bottle (up to 5, from the camera or files), read together by AI
   * when it's on, or the name typed; the RxNorm match picked and confirmed by the nurse; how it's
   * taken in the home and what was prescribed. Nothing is saved until the nurse confirms.
   */
  function addMedication() {
    const photos: UploadedFile[] = [];
    let readByAi = false;
    type Match = {
      rxcui: string;
      name: string;
      strength: string | null;
      doseForm: string | null;
      ingredient: string | null;
    };
    let matches: Match[] = [];
    let picked: Match | null = null;
    const thumbs = el("div", { class: "photopick", "aria-label": "Bottle photos" });
    const status = el("p", { class: "small", role: "status", "aria-live": "polite" });
    const max = data.medications.maxPhotos;
    const up = uploadControl({
      kind: "patient",
      id: patientId,
      camera: true,
      label: "Add photo",
      icon: "device-camera",
      hint: `Up to ${max} photos of one bottle, from different sides`,
      onUploaded: (f) => {
        if (photos.length >= max) return toast(`Up to ${max} photos of one bottle`);
        photos.push(f);
        thumbs.append(chip(`Photo ${photos.length}`, "plain"));
        readBtn.disabled = false;
      },
    });
    const readBtn = btn("Read the photos", "sparkles-fill", async () => {
      readBtn.disabled = true;
      status.replaceChildren("AI is reading the photos together…");
      const res = await api<{
        read: {
          name: string | null;
          strength: string | null;
          form: string | null;
          directions: string | null;
        };
        matches: Match[];
        matchError?: string;
        error?: string;
      }>("POST", "/api/ai/bottle", { patient: patientId, files: photos.map((f) => f.id) });
      readBtn.disabled = false;
      if (!res.ok)
        return status.replaceChildren(
          res.body.error ?? "AI couldn't read it. Type the name instead.",
        );
      readByAi = true;
      const r = res.body.read;
      if (r.name) name.input.value = [r.name, r.strength].filter(Boolean).join(" ");
      if (r.directions && !inHome.input.value) inHome.input.value = r.directions;
      status.replaceChildren(
        el("span", { class: "chip info small", "data-ai": "1" }, "Read by AI, check it"),
        ` ${[r.name, r.strength, r.form].filter(Boolean).join(" · ") || "Nothing could be read"}`,
        res.body.matchError ? ` · ${res.body.matchError}` : "",
      );
      showMatches(res.body.matches);
    });
    readBtn.disabled = true;
    const name = field("Medication name and strength", "", {
      maxlength: "120",
      placeholder: "e.g. lisinopril 10 mg",
    });
    const find = btn("Find in RxNorm", "search", async () => {
      const res = await api<{ matches: Match[]; error?: string }>(
        "GET",
        `/api/drugs?q=${encodeURIComponent(name.input.value)}`,
      );
      if (!res.ok)
        return status.replaceChildren(
          res.body.error ?? "The drug references didn't answer. Try again.",
        );
      showMatches(res.body.matches);
    });
    const matchBox = el("div", {
      class: "matchpick",
      role: "radiogroup",
      "aria-label": "RxNorm match",
    });
    function showMatches(list: Match[]) {
      matches = list;
      picked = null;
      confirm.checked = false;
      matchBox.replaceChildren(
        ...(list.length
          ? list.map((mt, i) => {
              const r = el("input", {
                type: "radio",
                name: "rxmatch",
                value: mt.rxcui,
                id: `rx-${i}`,
              });
              r.addEventListener("change", () => {
                picked = matches[i] ?? null;
              });
              return el(
                "label",
                { for: `rx-${i}` },
                r,
                el(
                  "span",
                  {},
                  el("b", {}, mt.name),
                  el("br"),
                  el("span", { class: "small muted" }, `RxNorm ${mt.rxcui}`),
                ),
              );
            })
          : [
              el(
                "p",
                { class: "small muted" },
                "No match in RxNorm. Check the spelling, or type it as it reads.",
              ),
            ]),
      );
    }
    // Brief 1E-28: the medication's own fields (the name comes from the match).
    const own = medFields();
    own.name.el.hidden = true;
    const inHome = own.freq;
    const covered = select(
      "Covered by claim",
      [
        ["", "Not known"],
        ["yes", "Covered"],
        ["no", "Not covered"],
      ] as const,
      "",
    );
    const confirm = el("input", { type: "checkbox", id: "med-confirm" });
    const save = btn(
      "Confirm and add",
      "check",
      async () => {
        if (!picked) return refuse(m.note, "Pick the RxNorm match.");
        if (!confirm.checked) return refuse(m.note, "Confirm the match against the bottle.");
        save.disabled = true;
        const res = (await api<Tab>("POST", `${base}/medications`, {
          ...picked,
          confirmed: true,
          files: photos.map((f) => f.id),
          readByAi,
          ...own.own(),
          startedOn: own.started.input.value || null,
          ...(data.medications.claim && covered.input.value
            ? { coveredByClaim: covered.input.value === "yes" }
            : {}),
        })) as Res;
        save.disabled = false;
        if (!res.ok) return refuse(m.note, res.body.error);
        m.close();
        void took(res, "Medication added");
      },
      "btn pri",
    );
    const m = openModal({
      title: "Add medication",
      sub: "Photograph the bottle from each side, or type the name.",
      wide: true,
      body: [
        el(
          "div",
          { class: "form", style: "max-width:none" },
          el("h3", { class: "h3s" }, "Photos of the bottle"),
          up.el,
          thumbs,
          readingOn ? readBtn : null,
          status,
          el("h3", { class: "h3s" }, "The match"),
          el(
            "div",
            { class: "formgrid" },
            name.el,
            el("div", { class: "field" }, el("label", {}, " "), find),
          ),
          matchBox,
          own.grid,
          data.medications.claim ? el("div", { class: "formgrid" }, covered.el) : null,
          el(
            "label",
            { for: "med-confirm", style: "display:flex;gap:6px;align-items:center" },
            confirm,
            "I checked this match against the bottle",
          ),
        ),
      ],
      actions: [save],
    });
  }

  // ---- Skin and wounds ---------------------------------------------------------------------------------

  function measureFields() {
    const length = field("Length (cm)", "", { type: "number", min: "0", step: "0.1" });
    const width = field("Width (cm)", "", { type: "number", min: "0", step: "0.1" });
    const depth = field("Depth (cm)", "", { type: "number", min: "0", step: "0.1" });
    const drainage = select(
      "Drainage",
      [["", "–"], ...data.choices.drainages.map((d) => [d, d] as const)],
      "",
    );
    const notes = area("Notes", "");
    const photos: UploadedFile[] = [];
    const list = el("div", { class: "photopick" });
    const up = uploadControl({
      kind: "patient",
      id: patientId,
      camera: true,
      label: "Add photo",
      icon: "device-camera",
      onUploaded: (f) => {
        photos.push(f);
        list.append(chip(f.name, "plain"));
      },
    });
    return {
      els: [
        el("div", { class: "formgrid" }, length.el, width.el, depth.el, drainage.el),
        notes.el,
        up.el,
        list,
      ],
      value: () => ({
        lengthCm: length.input.value,
        widthCm: width.input.value,
        depthCm: depth.input.value || null,
        drainage: drainage.input.value || null,
        notes: notes.input.value,
        photos: photos.map((f) => f.id),
      }),
      focus: () => length.input.focus(),
    };
  }

  function addWound() {
    const location = select(
      "Location",
      data.choices.woundLocations.map((l) => [l, l] as const),
      data.choices.woundLocations[0] ?? "",
    );
    const kind = select(
      "Kind",
      data.choices.woundKinds.map((k) => [k, k] as const),
      data.choices.woundKinds[0] ?? "",
    );
    const mf = measureFields();
    // Brief 1E-28: measurements are optional; where it was noted from and the treatment.
    const noted = select(
      "Noted from",
      data.notedFrom.map((n) => [n.value, n.label] as const),
      "seen",
    );
    const treatment = area("Treatment", "");
    treatment.input.placeholder = "e.g. Clean with saline, foam dressing, change every 3 days";
    const save = btn(
      "Save",
      "check",
      async () => {
        const res = (await api<Tab>("POST", `${base}/wounds`, {
          location: location.input.value,
          kind: kind.input.value,
          notedFrom: noted.input.value,
          treatment: treatment.input.value,
          measure: mf.value(),
        })) as Res;
        if (!res.ok) return refuse(m.note, res.body.error);
        m.close();
        void took(res, "Wound saved");
      },
      "btn pri",
    );
    const m = openModal({
      title: "Add a wound",
      wide: true,
      body: [
        el(
          "div",
          { class: "form", style: "max-width:none" },
          el("div", { class: "formgrid" }, location.el, kind.el, noted.el),
          listLink("wound_kind"),
          treatment.el,
          el("h3", { class: "h3s" }, "Measurements (if known)"),
          ...mf.els,
        ),
      ],
      actions: [save],
    });
  }

  function addMeasure(w: Tab["wounds"][number]) {
    const mf = measureFields();
    const save = btn(
      "Save measurement",
      "check",
      async () => {
        const res = (await api<Tab>("POST", `${base}/wounds/${w.id}/measures`, mf.value())) as Res;
        if (!res.ok) return refuse(m.note, res.body.error);
        m.close();
        void took(res, "Measurement saved");
      },
      "btn pri",
    );
    const m = openModal({
      title: `${w.location} · ${w.kind}`,
      sub: "Measured and dated today.",
      wide: true,
      body: [el("div", { class: "form", style: "max-width:none" }, ...mf.els)],
      actions: [save],
    });
    mf.focus();
  }

  function closeWound(w: Tab["wounds"][number]) {
    const reason = field("Why it's closed", "Healed", { maxlength: "300" });
    const save = btn(
      "Close the wound",
      "check",
      async () => {
        const res = (await api<Tab>("POST", `${base}/wounds/${w.id}/close`, {
          reason: reason.input.value,
        })) as Res;
        if (!res.ok) return refuse(m.note, res.body.error);
        m.close();
        void took(res, "Wound closed");
      },
      "btn pri",
    );
    const m = openModal({
      title: `Close ${w.location}`,
      sub: "The wound is marked closed with the reason given.",
      body: [el("div", { class: "form" }, reason.el)],
      actions: [save],
    });
  }

  function woundBlock(): Node[] {
    const out: Node[] = data.wounds.length
      ? data.wounds.map((w) => {
          const last = w.measures[w.measures.length - 1];
          const photos = w.measures.flatMap((m) => m.photos.map((p) => ({ ...p, on: m.on })));
          const add =
            data.canEdit && !w.closed
              ? (() => {
                  const b = el(
                    "button",
                    {
                      type: "button",
                      class: "ph add",
                      "aria-label": `Measure ${w.location}`,
                      "data-tip": "Add a measurement and photo",
                    },
                    icon("plus"),
                    el("small", {}, "Photo"),
                  );
                  b.addEventListener("click", () => addMeasure(w));
                  return b;
                })()
              : null;
          return el(
            "div",
            { class: "wound" },
            el(
              "div",
              {},
              el(
                "div",
                { class: "wph" },
                ...photos.map((p) => {
                  const b = el(
                    "button",
                    {
                      type: "button",
                      class: "ph",
                      "data-tip": "View photo",
                      "aria-label": `Photo ${p.on}`,
                    },
                    icon("file-media"),
                    el("small", {}, p.on.slice(0, 5)),
                  );
                  b.addEventListener(
                    "click",
                    () => void openViewer({ file: p, document: `Wound photo · ${w.location}` }),
                  );
                  return b;
                }),
                add,
              ),
              w.measures.length
                ? table(
                    [
                      { h: "Date", f: (m: (typeof w.measures)[number]) => m.on },
                      { h: "Size", f: (m) => m.size },
                      {
                        h: "Trend",
                        f: (m) =>
                          m.trend === "Larger"
                            ? chip(m.trend, "warn")
                            : m.trend === "Smaller"
                              ? chip(m.trend, "ok")
                              : m.trend,
                      },
                      { h: "Drainage", f: (m) => m.drainage ?? "–" },
                    ],
                    w.measures,
                    undefined,
                    `Measurements of ${w.location}`,
                  )
                : null,
            ),
            el(
              "div",
              {},
              kv([
                ["Location", w.location],
                ["Type", w.kind],
                ...(w.notedFrom ? ([["Noted from", w.notedFrom]] as [string, string][]) : []),
                ...(w.treatment ? ([["Treatment", w.treatment]] as [string, string][]) : []),
                ["Size", last?.size ?? "Not measured yet"],
                ["Drainage", last?.drainage ?? "–"],
                [
                  "Status",
                  w.closed
                    ? `Closed ${w.closed.on} · ${w.closed.reason ?? ""}`
                    : `Open since ${w.opened}`,
                ],
              ]),
              data.canEdit && !w.closed
                ? el(
                    "span",
                    { class: "rowbtns" },
                    ib("plus", "Add a measurement", () => addMeasure(w)),
                    ib("check-circle", "Close the wound", () => closeWound(w)),
                  )
                : null,
            ),
          );
        })
      : [el("p", { class: "muted" }, "No wounds. Skin intact.")];
    if (data.canEdit)
      out.push(
        el(
          "div",
          { class: "actions", style: "margin-top:8px" },
          btn("Add a wound", "plus", addWound),
        ),
      );
    return out;
  }

  // ---- Appointments and care team -------------------------------------------------------------------

  /** Add appointment: the one appointment form (appointment-form.ts), also on Next steps. */
  function addAppointment() {
    appointmentForm(patientId, data, (res) => took(res, "Appointment saved"));
  }

  /** Add to the care team (mockup v86 ctModal): also in the patient's Contacts unless unticked. */
  function addCareTeam() {
    const role = select(
      "Role",
      data.choices.careTeamRoles.map((r) => [r, r] as const),
      data.choices.careTeamRoles[0] ?? "",
    );
    const name = field("Name", "", { maxlength: "160" });
    const org = field("Organization", "", { maxlength: "160" });
    const phone = field("Phone", "", { maxlength: "14", inputmode: "tel" });
    const fax = field("Fax", "", { maxlength: "14", inputmode: "tel" });
    const email = field("Email", "", { maxlength: "200", type: "email" });
    const notes = area("Notes", "");
    const inContacts = el("input", { type: "checkbox", checked: true, id: "ct-contacts" });
    const save = btn(
      "Save",
      "check",
      async () => {
        const res = (await api<Tab>("POST", `${base}/care-team`, {
          role: role.input.value,
          name: name.input.value,
          organization: org.input.value,
          phone: phone.input.value,
          fax: fax.input.value,
          email: email.input.value,
          notes: notes.input.value,
          inContacts: inContacts.checked,
        })) as Res;
        if (!res.ok) return refuse(m.note, res.body.error);
        m.close();
        void took(res, "Added to the care team");
        changed();
      },
      "btn pri",
    );
    const m = openModal({
      title: "Add to the care team",
      wide: true,
      body: [
        el(
          "div",
          { class: "form", style: "max-width:none" },
          el("div", { class: "formgrid" }, role.el, name.el, org.el, phone.el, fax.el, email.el),
          listLink("care_team_role"),
          notes.el,
          el(
            "label",
            { class: "tsr", for: "ct-contacts" },
            inContacts,
            " Also in the patient's Contacts",
          ),
        ),
      ],
      actions: [save],
    });
    name.input.focus();
  }

  function teamBlock(): Node[] {
    const flagged = data.appointments.filter((a) => a.office).length;
    const out: Node[] = [];
    if (flagged)
      out.push(
        el(
          "div",
          { class: "note" },
          el(
            "p",
            {},
            icon("location"),
            " ",
            el("b", {}, `${flagged} appointment${flagged === 1 ? "" : "s"} flagged for the office`),
            ": a ride or an aide to arrange.",
          ),
        ),
      );
    out.push(
      data.appointments.length
        ? table(
            [
              {
                h: "Appointment",
                f: (a: Tab["appointments"][number]) =>
                  el(
                    "span",
                    {},
                    el("b", {}, a.withWhom),
                    el("br"),
                    el(
                      "span",
                      { class: "small muted" },
                      [a.kind, a.provider, a.place, a.notes].filter(Boolean).join(" · "),
                    ),
                  ),
              },
              {
                h: "When",
                f: (a) =>
                  el(
                    "span",
                    {},
                    a.when,
                    a.backAt ? ` · back at ${a.backAt}` : "",
                    a.repeats ? el("br") : null,
                    a.repeats ? el("span", { class: "small muted" }, a.repeats) : null,
                  ),
              },
              {
                h: "Ride",
                f: (a) => {
                  if (!data.canEdit) return data.rides.find((r) => r.value === a.ride)?.label ?? "";
                  const s = el(
                    "select",
                    { "aria-label": `Ride for ${a.withWhom}` },
                    ...data.rides.map((r) =>
                      el("option", { value: r.value, selected: r.value === a.ride }, r.label),
                    ),
                  );
                  s.addEventListener(
                    "change",
                    () =>
                      void send(
                        "PUT",
                        `/appointments/${a.id}`,
                        { ride: s.value },
                        s.value === "needed" ? "Flagged for the office" : "Saved",
                      ),
                  );
                  return el(
                    "span",
                    {},
                    s,
                    a.ride === "needed" ? chip("Flagged for the office", "warn") : null,
                  );
                },
              },
              {
                h: "Aide during it",
                f: (a) => {
                  if (!data.canEdit) return data.aides.find((r) => r.value === a.aide)?.label ?? "";
                  const s = el(
                    "select",
                    { "aria-label": `Aide during ${a.withWhom}` },
                    ...data.aides.map((r) =>
                      el("option", { value: r.value, selected: r.value === a.aide }, r.label),
                    ),
                  );
                  s.addEventListener(
                    "change",
                    () => void send("PUT", `/appointments/${a.id}`, { aide: s.value }, "Saved"),
                  );
                  return s;
                },
              },
            ],
            data.appointments,
            undefined,
            "Appointments",
          )
        : el("p", { class: "muted" }, "No appointments."),
    );
    out.push(
      el("h3", { class: "h3s" }, "Care team"),
      data.careTeam.length
        ? table(
            [
              {
                h: "Who",
                f: (c: Tab["careTeam"][number]) =>
                  el(
                    "span",
                    {},
                    el("b", {}, c.role),
                    el("br"),
                    el(
                      "span",
                      { class: "small muted" },
                      [c.name, c.organization].filter(Boolean).join(" · "),
                    ),
                    c.notes ? el("div", { class: "small muted" }, c.notes) : null,
                  ),
              },
              {
                h: "Phone",
                f: (c) =>
                  [c.phone, c.fax ? `fax ${c.fax}` : null, c.email].filter(Boolean).join(" · ") ||
                  "–",
              },
              {
                h: "",
                f: (c) =>
                  data.canEdit
                    ? ib("archive", `Take ${c.name} off the care team`, () => {
                        void send(
                          "POST",
                          `/care-team/${c.id}/archive`,
                          { reason: "No longer on the care team" },
                          "Taken off the care team",
                        );
                      })
                    : "",
              },
            ],
            data.careTeam,
            undefined,
            "Care team",
          )
        : el("p", { class: "muted small" }, "No one yet."),
    );
    if (data.canEdit)
      out.push(
        el(
          "div",
          { class: "actions", style: "margin-top:8px" },
          btn("Add appointment", "plus", addAppointment),
          btn("Add to the care team", "person-add", addCareTeam),
        ),
      );
    return out;
  }

  // ---- The tab ------------------------------------------------------------------------------------------

  function draw() {
    const a = opened ? data.assessments.find((x) => x.id === opened) : null;
    if (a) {
      host.replaceChildren(assessmentView(a));
      return;
    }
    opened = null;
    host.replaceChildren(
      listCard(),
      card(
        el("span", {}, icon("checklist"), " Medication reconciliation"),
        "Compare what's taken in the home with each list.",
        [],
        ...medBlock(),
      ),
      card("Skin and wounds", "Location, size and photos over time.", [], ...woundBlock()),
      card(
        "Appointments and care team",
        "A ride or an aide during an appointment is flagged for the office.",
        [],
        ...teamBlock(),
      ),
    );
  }

  draw();
}
